import { inject, injectable } from 'inversify';
import type { types } from 'mediasoup-client';
import { serializeError } from 'serialize-error';
import WebRTCIssueDetector from 'webrtc-issue-detector';
import { ClientMetaProvider } from '../ClientMetaProvider';
import {
  FIVE_SECONDS_IN_MS,
  SIGNALING_API_VERSION,
} from '../constants/common';
import {
  CLIENT_EVENTS, INTERNAL_CLIENT_EVENTS, NETWORK_OBSERVER_EVENTS,
} from '../constants/events';
import { MutexKey } from '../constants/mutex';
import EnhancedEventEmitter from '../EnhancedEventEmitter';
import { isExpectedSignalingError } from '../errors/isExpectedSignalingError';
import LivedigitalSDKError from '../errors/LivedigitalSDKError';
import NeedJoinFirstError from '../errors/NeedJoinFirstError';
import OperationError from '../errors/OperationError';
import UnsupportedError from '../errors/UnsupportedError';
import validateAppData from '../helpers/appDataValidator';
import { detectBrowserEnvironment } from '../helpers/browser';
import { markSDKInstance, safeCall } from '../helpers/common';
import { getSupportedCodecsMimeTypes } from '../helpers/media';
import { randomString } from '../helpers/randomString';
import { retryAsync } from '../helpers/retry';
import { TOKEN } from '../inversify.tokens';
import {
  CreateCameraVideoTrackOptions,
  CreateCustomAudioOptions,
  CreateCustomVideoOptions,
  CreateMicrophoneAudioTrackOptions,
  CreateScreenMediaOptions,
  JoinChannelParams,
  Role,
  TrackLabel,
} from '../types/common';
import type { PeerTrackFactory } from '../types/container';
import {
  ClientObserverEvents,
  InternalObserverEvents,
} from '../types/engine';
import {
  AudioTrack,
  BaseTrack,
  Track,
} from '../types/media';
import type { LDTransport } from '../types/mediasoup';
import { CallState } from './CallState';
import EventsQueue from './EventsQueue';
import ChannelEventHandler from './handlers/ChannelEventHandler';
import MediaEventHandler from './handlers/MediaEventHandler';
import { IntegrationsService } from './IntegrationsService';
import Logger from './Logger';
import Media from './media';
import { ProcessorsCache } from './media/streamEffects/ProcessorsCache';
import VideoTrack from './media/tracks/DefaultVideoTrack';
import ProducerCloseManager from './media/producer/ProducerCloseManager';
import { MyPeer } from './MyPeer';
import Network from './network';
import NetworkTransportPolicyManager from './network/NetworkTransportPolicyManager';
import Peer from './Peer';
import type { IPeersService } from './Peers';
import LocalMutex from './LocalMutex';
import SignalingApiClient from './signalingApiClient/SignalingApiClient';
import System from './system';
import ConnectionStatManager from './WebRTCStats/ConnectionStatsManager';
import StatsHandler from './WebRTCStats/StatsHandler';
import TransportsStatsProvider from './WebRTCStats/TransportsStatsProvider';
import { TransportsWebRTCStats } from './WebRTCStats/types';
import PeersStorage from './PeersStorage';
import { ScoresCalculator } from '../qoe/ScoresCalculator';

@injectable()
class Engine {
  readonly #logger: Logger;

  private rejoinRequested = false;

  private isReleasing = false;

  #isConnectionLost = false;

  readonly #denoiserInitializing = false;

  private readonly mark = randomString();

  private readonly handleSignalingError = (error: Error): void => {
    if (this.isReleasing || !this.isChannelJoined || this.state.isChannelJoining) {
      return;
    }

    if (error instanceof NeedJoinFirstError) {
      this.triggerRejoinEventIfAppropriate(error.errorCode);
    }
  };

  constructor(
    @inject(TOKEN.CallState) private readonly state: CallState,
    @inject(TOKEN.MyPeer) private readonly myPeer: MyPeer,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
    @inject(TOKEN.IntegrationsService) private readonly integrationsService: IntegrationsService,
    @inject(TOKEN.ProcessorsCache) private readonly processorsCache: ProcessorsCache,
    @inject(TOKEN.MediaEventHandler) private readonly mediaEventHandler: MediaEventHandler,
    @inject(TOKEN.ChannelEventHandler) private readonly channelEventsHandler: ChannelEventHandler,
    @inject(TOKEN.PeerTrackFactory) private readonly peerTrackFactory: PeerTrackFactory,
    @inject(TOKEN.Peers) private readonly peers: IPeersService,
    @inject(TOKEN.PeersStorage) private readonly peersStorage: PeersStorage,
    @inject(TOKEN.System) readonly system: System,
    @inject(TOKEN.Media) readonly media: Media,
    @inject(TOKEN.Network) readonly network: Network,
    @inject(TOKEN.NetworkTransportPolicyManager)
    private readonly networkTransportPolicyManager: NetworkTransportPolicyManager,
    @inject(TOKEN.ProducerCloseManager) private readonly producerCloseManager: ProducerCloseManager,
    @inject(TOKEN.SignalingApiClient) private readonly signalingApiClient: SignalingApiClient,
    @inject(TOKEN.EventsQueue) private readonly eventsQueue: EventsQueue,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter<ClientObserverEvents>,
    @inject(TOKEN.InternalEventEmitter)
    private readonly internalEventEmitter: EnhancedEventEmitter<InternalObserverEvents>,
    @inject(TOKEN.StatsHandler) private readonly statsHandler: StatsHandler,
    @inject(TOKEN.TransportsStatsProvider) private readonly transportsStatsProvider: TransportsStatsProvider,
    @inject(TOKEN.LocalMutex) private readonly localMutex: LocalMutex,
    @inject(TOKEN.ScoresCalculator) private readonly scoresCalculatorInstance: ScoresCalculator,
    @inject(TOKEN.ClientMetaProvider) private readonly clientMetaProvider: ClientMetaProvider,
    @inject(TOKEN.WebRTCIssueDetector) private readonly webRtcIssueDetector?: WebRTCIssueDetector,
  ) {
    this.#logger = loggerFactory('Engine');

    this.watchNetworkState();
    this.watchDocumentUnload();
    this.watchInternalEvents();
    this.signalingApiClient.addErrorListener(this.handleSignalingError);

    markSDKInstance(this.mark);
  }

  get isChannelJoined(): boolean {
    return this.state.isJoined;
  }

  get scoresCalculator(): ScoresCalculator {
    return this.scoresCalculatorInstance;
  }

  private async initialize(): Promise<void> {
    try {
      const { data } = await this.signalingApiClient.router.getRtpCapabilities();
      const { rtpCapabilities } = data;
      this.integrationsService.init();
      await this.media.loadDevice(rtpCapabilities as unknown as types.RtpCapabilities);

      this.#logger.info('Supported capabilities', {
        codecs: getSupportedCodecsMimeTypes(this.media.mediasoupDevice),
      });

      const [sendTransport, recvTransport] = await this.createSendRecvTransports();
      this.webRtcIssueDetector?.handleNewPeerConnection(sendTransport.handler._pc, sendTransport.id);
      this.webRtcIssueDetector?.handleNewPeerConnection(recvTransport.handler._pc, recvTransport.id);

      this.transportsStatsProvider.init({
        media: this.media,
        peers: this.peers,
        receiveTransportConnectionManager: new ConnectionStatManager({
          transport: recvTransport,
          statsHandler: this.statsHandler,
        }),
        sendTransportConnectionManager: new ConnectionStatManager({
          transport: sendTransport,
          statsHandler: this.statsHandler,
        }),
      });

      await this.network.initializeHeartbeats();
      const { isChromiumBased } = detectBrowserEnvironment();

      if (!isChromiumBased) {
        await this.network.createSignalingTransport(this.media.mediasoupDevice);
        await this.network.createKeepAliveConsumer();
      }

      await this.network.createAllDataChannels();

      if (this.network.channelMessagesDataConsumer) {
        this.eventsQueue.setChannelEventHandler(
          this.channelEventsHandler.handleChannelEvent.bind(this.channelEventsHandler),
        );

        this.eventsQueue.setMediaEventHandler(
          this.mediaEventHandler.handleMediaEvent.bind(this.mediaEventHandler),
        );

        this.eventsQueue.subscribe(this.network.channelMessagesDataConsumer);
      }

      this.#logger.debug('initialize()', {
        sendTransportId: this.network.sendTransport?.id,
        recvTransportId: this.network.receiveTransport?.id,
      });
    } catch (error) {
      this.#logger.error('initialize()', { error });

      const isDeviceNotSupported = error instanceof Error
        && error.name === 'UnsupportedError'
        && error.message === 'device not supported';

      const isNoRTCPeerConnection = error instanceof Error
        && error.message === 'RTCPeerConnection is not defined';

      if (isDeviceNotSupported || isNoRTCPeerConnection) {
        throw new UnsupportedError('Device not supported', UnsupportedError.UNSUPPORTED_DEVICE_CODE);
      }

      if (error instanceof LivedigitalSDKError) {
        throw error;
      }

      throw new OperationError('initialize_engine_error', error);
    }
  }

  public async release(keepTracks?: boolean): Promise<void> {
    if (this.isReleasing) {
      return;
    }

    const myPeer = this.myPeer.get();
    const logCtx = {
      case: 'release',
      applicationId: this.state.appId,
      channelId: this.state.channelId,
      peerId: myPeer?.id ?? this.myPeerId,
      peerRole: myPeer?.role,
      isJoined: this.state.isJoined,
      isChannelJoining: this.state.isChannelJoining,
      rejoinRequested: this.rejoinRequested,
    };

    this.isReleasing = true;

    const releaseLock = await this.localMutex.lock(MutexKey.Release);

    try {
      this.signalingApiClient.abortPendingRequests('release');

      if (!keepTracks) {
        this.media.clearTracks();
      }

      await Promise.all([
        safeCall(async () => await this.peers.reset()),
        safeCall(async () => await this.media.unpublishTracks()),
        safeCall(async () => await this.signalingApiClient.peers.disconnectPeer()),
      ]);

      this.cleanup();
      this.#logger.debug('release()', logCtx);
    } catch (error: unknown) {
      this.#logger.error('Failed to release', { ...logCtx, error: serializeError(error) });
    } finally {
      this.isReleasing = false;
      releaseLock();
    }
  }

  public getPeers(): Peer[] {
    return this.peers.all();
  }

  public get myPeerId(): string | undefined {
    return this.myPeer.id;
  }

  public async confirmActivity(): Promise<void> {
    try {
      await this.signalingApiClient.channel.confirmChannelActivity();
    } catch (error) {
      throw new OperationError('confirm_activity_failed', error);
    }
  }

  public async join({ token, appData, isP2pCall, preferredDC }: JoinChannelParams): Promise<void> {
    if (this.isChannelJoined) {
      throw new OperationError('method_not_available_when_joined');
    }

    const releaseLock = await this.localMutex.lock(MutexKey.Join);

    try {
      this.#logger.debug('join()', { appData, isP2pCall });
      this.state.isChannelJoining = true;
      this.signalingApiClient.abortPendingRequests('join');
      this.signalingApiClient.setSecurityData({
        token,
        peerApiVersion: SIGNALING_API_VERSION,
        datacenterType: isP2pCall ? 'p2p' : 'common',
        preferredDC: preferredDC,
      });

      const { data, headers } = await this.signalingApiClient.peers.registerPeer({ appData });
      const {
        appId,
        channelId,
        peerId,
      } = data;
      const datacenterName = String(headers['x-datacenter-name']);
      const nodeId = String(headers['x-node-id']);

      this.myPeer.id = peerId;
      this.state.appId = appId;
      this.state.channelId = channelId;
      this.clientMetaProvider.setPeerId(peerId);
      this.clientMetaProvider.setChannelId(channelId);
      this.clientMetaProvider.setAppId(appId);
      this.clientMetaProvider.setDatacenterName(datacenterName);
      this.clientMetaProvider.setNodeId(nodeId);

      await this.initialize();
      await this.network.waitForReceiveTransportConnected({
        onReceiveTransportFailed: async () => {
          await this.networkTransportPolicyManager.escalateDirectionToRelay('recv');
        },
      });
      await this.network.waitForChannelMessagesDataConsumerConnected();

      this.state.isJoined = true;
      this.networkTransportPolicyManager.startMonitoring();
      this.#logger.info('Successfully joined channel', { channelId, datacenterName });
    } catch (error) {
      this.signalingApiClient.abortPendingRequests('join');
      await this.release();
      this.#logger.error('join()', { error });
      throw new OperationError('join_failed', error);
    } finally {
      this.state.isChannelJoining = false;
      this.rejoinRequested = false;
      releaseLock();
    }
  }

  public async loadPeers(role?: Role): Promise<void> {
    try {
      await this.peers.load(role);
    } catch (error) {
      throw new OperationError('load_peers_failed', error);
    }
  }

  public requestChannelStateSync(): void {
    const triggerChannelStateEventWithRetry = async (): Promise<void> => await new Promise(async (resolve, reject) => {
      if (!this.isChannelJoined || this.rejoinRequested || this.state.isChannelJoining) {
        return resolve();
      }

      const messageId = await this.network.triggerChannelStateEvent('host');
      const timeout = setTimeout(() => {
        if (this.eventsQueue.isMessageReceived(messageId)) {
          this.#logger.info('Channel state event received', {
            case: 'Engine.requestChannelStateSync',
          });

          resolve();
        } else {
          reject(new OperationError(
            'channel_state_event_not_received',
            new Error('Channel state event not received'),
          ));
        }
      }, FIVE_SECONDS_IN_MS);

      const abortOnConnectionLost = (): void => {
        clearTimeout(timeout);
        this.clientEventEmitter.off(CLIENT_EVENTS.connectionLost, abortOnConnectionLost);
        resolve();
      };

      this.clientEventEmitter.on(CLIENT_EVENTS.connectionLost, abortOnConnectionLost);
    });

    retryAsync(triggerChannelStateEventWithRetry, {
      minBackoffDelayMs: 0,
      maxBackoffDelayMs: 0,
      maxRetries: 10,
      actionName: 'triggerChannelStateEventWithRetry',
      logger: this.#logger,
    }).catch((error: unknown) => {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.error.bind(this.#logger);
      logAction('Failed to request channel state event', {
        case: 'Engine.requestChannelStateSync',
        error: serializeError(error),
      });
    });
  }

  public async updateAppData(appData: Record<string, unknown>): Promise<void> {
    const myPeer = this.myPeer.get();

    if (!myPeer) {
      throw new OperationError('need_joined_to_channel', new Error('Need join to channel first'));
    }

    try {
      validateAppData(appData);
    } catch (error) {
      this.#logger.error('updateAppData() validation failed', { error });
      throw new OperationError('invalid_app_data', error);
    }

    try {
      await this.network.updatePeerAppData({ peerId: myPeer.id, appData });
    } catch (error) {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.error.bind(this.#logger);
      logAction('updateAppData()', { error: serializeError(error) });
      throw new OperationError('update_app_data_failed', error);
    }
  }

  async createCameraVideoTrack(options?: CreateCameraVideoTrackOptions): Promise<VideoTrack> {
    const trackParams = Media.getCameraVideoTrackParams(options);

    try {
      const [track] = await this.media.createUserMediaTracks({
        audio: false,
        video: trackParams.videoTrackOptions,
      });

      track.setStopTrackOnPause(Boolean(options?.stopTrackOnPause));

      if (track instanceof VideoTrack) {
        track.setLabel(options?.isPreview ? TrackLabel.CameraPreview : TrackLabel.Camera);
        track.setEncoderConfig(trackParams.encoderConfig);
        track.setTransformParams(Media.getVideoTrackTransformParams(track.mediaStreamTrack));
      }

      this.media.setTrack(track);
      this.#logger.debug('createCameraVideoTrack()', { trackParams });

      return track as unknown as VideoTrack;
    } catch (error) {
      this.#logger.error('createCameraVideoTrack()', { error, trackParams });
      throw new OperationError('create_camera_track_failed', error);
    }
  }

  async createMicrophoneAudioTrack(options?: CreateMicrophoneAudioTrackOptions): Promise<AudioTrack> {
    try {
      const [track] = await this.media.createUserMediaTracks({
        video: false,
        audio: options?.audio,
      });

      const trackLabel = options?.isPreview ? TrackLabel.MicrophonePreview : TrackLabel.Microphone;

      track.setLabel(trackLabel);
      track.setStopTrackOnPause(Boolean(options?.stopTrackOnPause));

      if (options?.encoderConfig) {
        track.setEncoderConfig(options.encoderConfig);
      }

      if (track.kind === 'audio' && track.getLabel() === TrackLabel.Microphone && !this.#denoiserInitializing) {
        this.media.initDenoiser(track as AudioTrack, options).catch((error: unknown) => {
          this.clientEventEmitter.emit(CLIENT_EVENTS.denoiserFailed, { cause: 'Failed to initialize denoiser', error });
          this.#logger.error('Failed to execute initDenoiser', {
            case: 'Denoiser',
            options,
            error: serializeError(error),
          });
        }); // must be non-blocking, don't await
      }

      this.media.setTrack(track);
      this.#logger.debug('createMicrophoneAudioTrack()', {
        options,
        trackLabel: track.getLabel(),
      });

      return track as AudioTrack;
    } catch (error) {
      this.#logger.error('createMicrophoneAudioTrack()', { error, options });
      throw new OperationError('create_microphone_track_failed', error);
    }
  }

  async createScreenMediaTracks(options?: CreateScreenMediaOptions): Promise<Track[]> {
    const videoTrackParams = Media.getScreenVideoTrackParams(options);

    try {
      const tracks = await this.media.createDisplayMediaTracks({
        audio: options?.audio,
        video: options?.video,
      });

      tracks.forEach((track) => {
        track.mediaStreamTrack.addEventListener('ended', () => {
          track.unpublish().catch((error: unknown) => {
            this.#logger.error('Failed to unpublish screen track', {
              error: serializeError(error),
            });
          });
        }, { once: true });

        track.setStopTrackOnPause(Boolean(options?.stopTrackOnPause));

        if (track instanceof VideoTrack) {
          track.setLabel(TrackLabel.ScreenVideo);
          track.setEncoderConfig(videoTrackParams.encoderConfig);
          const transformParams = Media.getVideoTrackTransformParams(track.mediaStreamTrack);
          track.setTransformParams(transformParams);
          this.media.setTrack(track);
          this.#logger.debug('createScreenMediaTrack()', {
            trackParams: videoTrackParams,
            transformParams,
            label: track.logCtx,
          });

          return;
        }

        track.setLabel(TrackLabel.ScreenAudio);

        if (options?.audioEncoderConfig) {
          track.setEncoderConfig(options.audioEncoderConfig);
        }

        this.media.setTrack(track);
        this.#logger.debug('createScreenMediaTrack()', {
          trackParams: options?.audio,
          encoderConfig: options?.audioEncoderConfig,
        });
      });

      return tracks as Track[];
    } catch (error) {
      this.#logger.error('createScreenMediaTrack()', { error, trackParams: videoTrackParams });
      throw new OperationError('create_screen_tracks_failed', error);
    }
  }

  createCustomAudioTrack(options: CreateCustomAudioOptions): AudioTrack {
    if (!options.audioTrack) {
      throw new OperationError('missing_audio_track', new Error('options.audioTrack is required'));
    }

    try {
      const track = this.media.createCustomMediaTrack(options.audioTrack, {
        audio: options.audio,
      }) as AudioTrack;

      track.mediaStreamTrack.addEventListener('ended', () => {
        track.unpublish().catch((error: unknown) => {
          this.#logger.error('Failed to unpublish custom audio track', {
            track,
            error: serializeError(error),
          });
        });
      }, { once: true });

      track.setStopTrackOnPause(Boolean(options.stopTrackOnPause));

      track.setLabel(TrackLabel.CustomAudio);

      if (options.audioEncoderConfig) {
        track.setEncoderConfig(options.audioEncoderConfig);
      }

      this.media.setTrack(track);
      this.#logger.debug('createCustomAudioTrack()', {
        trackParams: options.audio,
        encoderConfig: options.audioEncoderConfig,
      });

      return track;
    } catch (error) {
      this.#logger.error('createCustomAudioTrack()', { error, trackParams: options.audioTrack });
      throw new OperationError('create_custom_audio_track_failed', error);
    }
  }

  createCustomVideoTrack(options: CreateCustomVideoOptions): Track {
    const videoTrackParams = Media.getScreenVideoTrackParams(options);

    if (!options.videoTrack) {
      throw new OperationError('missing_video_track', new Error('options.videoTrack is required'));
    }

    try {
      const track = this.media.createCustomMediaTrack(options.videoTrack, {
        video: options.video,
      }) as unknown as VideoTrack;

      track.mediaStreamTrack.addEventListener('ended', () => {
        track.unpublish().catch((error: unknown) => {
          this.#logger.error('Failed to unpublish custom video track', {
            track,
            error: serializeError(error),
          });
        });
      }, { once: true });

      track.mediaStreamTrack.contentHint = 'customVideo';
      track.setLabel(TrackLabel.CustomVideo);
      track.setEncoderConfig(videoTrackParams.encoderConfig);
      const transformParams = Media.getVideoTrackTransformParams(track.mediaStreamTrack);
      track.setTransformParams(transformParams);
      this.media.setTrack(track as unknown as BaseTrack);
      this.#logger.debug('createCustomVideoTrack()', {
        trackParams: videoTrackParams,
        transformParams,
        label: track.logCtx,
      });

      return track as unknown as Track;
    } catch (error) {
      this.#logger.error('createCustomVideoTrack()', { error, trackParams: videoTrackParams });
      throw new OperationError('create_custom_video_track_failed', error);
    }
  }

  async deleteTrack(tracks: BaseTrack): Promise<void> {
    try {
      await this.media.deleteTrack(tracks);
    } catch (error) {
      throw new OperationError('delete_track_failed', error);
    }
  }

  transportsStats(): TransportsWebRTCStats | undefined {
    return this.transportsStatsProvider.getStats();
  }

  setSignalingServerUrl(url: string): void {
    if (this.isChannelJoined || this.rejoinRequested || this.state.isChannelJoining) {
      throw new OperationError(
        'method_not_available_when_joined',
        new Error('Method not available when joined channel'),
      );
    }

    this.signalingApiClient.instance.defaults.baseURL = url;
  }

  private cleanup(): void {
    this.signalingApiClient.abortPendingRequests('cleanup');
    this.rejoinRequested = false;
    this.#isConnectionLost = false;
    this.networkTransportPolicyManager.stopMonitoring();
    this.state.reset();
    this.network.reset();
    this.peersStorage.clear();
    this.transportsStatsProvider.clear();
    this.statsHandler.clear();
    this.integrationsService.reset();
    this.webRtcIssueDetector?.stopWatchingNewPeerConnections();
    this.processorsCache.reset();
    this.clientMetaProvider.clear();
  }

  private watchInternalEvents(): void {
    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackProduced, async (track) => {
      await this.createSelfPeerTrack(track);
    });

    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackUnproduced, async (track) => {
      const peerTrack = this.myPeer.get()?.tracks.get(track.getLabel());
      peerTrack?.close()?.catch((error: unknown) => {
        this.#logger.debug('Failed to close peerTrack', {
          error: serializeError(error),
        });
      });
      await this.media.deleteTrack(track);
    });

    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackPaused, (track) => {
      const peerTrack = this.myPeer.get()?.tracks.get(track.getLabel());
      peerTrack?.pause()?.catch((error: unknown) => {
        this.#logger.debug('Failed to pause peerTrack', {
          error: serializeError(error),
        });
      });
    });

    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackResumed, (track) => {
      const peerTrack = this.myPeer.get()?.tracks.get(track.getLabel());
      peerTrack?.resume()?.catch((error: unknown) => {
        this.#logger.debug('Failed to resume peerTrack', {
          error: serializeError(error),
        });
      });
    });

    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackClosed, (track) => {
      const peerTrack = this.myPeer.get()?.tracks.get(track.getLabel());
      peerTrack?.close()?.catch((error: unknown) => {
        this.#logger.debug('Failed to close peerTrack', {
          error: serializeError(error),
        });
      });
    });

    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackReopened, async (track) => {
      await this.createSelfPeerTrack(track);
    });

    this.internalEventEmitter.on(INTERNAL_CLIENT_EVENTS.trackFailed, (track) => {
      this.clientEventEmitter.safeEmit(CLIENT_EVENTS.trackFailed, { label: track.getLabel() });
    });
  }

  private async createSelfPeerTrack(track: Track | BaseTrack): Promise<void> {
    if (!this.myPeer.id) {
      return;
    }

    const myPeer = this.peers.get((this.myPeer.id));

    if (!myPeer) {
      return;
    }

    // recreate track for the peer in case user changes camera/audio or admin turn on/off peers camera/mic
    await myPeer.tracks.get(track.getLabel())?.close();
    const peerTrack = this.peerTrackFactory({
      label: track.getLabel(),
      mediaStreamTrack: track.mediaStreamTrack,
      muted: false,
      paused: track.isPaused,
      onClose: () => { /* no operation */ },
      peerEventEmitter: myPeer.observer,
    });

    myPeer.tracks.set(peerTrack.label, peerTrack);
  }

  private triggerRejoinEventIfAppropriate(reason?: string): void {
    if (!this.isChannelJoined || this.isReleasing || this.state.isChannelJoining || this.rejoinRequested) {
      return;
    }

    this.#logger.debug('Triggering channel rejoin required event', { reason });

    this.signalingApiClient.abortPendingRequests('channel-rejoin');
    this.rejoinRequested = true;
    this.clientEventEmitter.emit(CLIENT_EVENTS.channelRejoinRequired);
  }

  private watchNetworkState(): void {
    this.network.observer.on(NETWORK_OBSERVER_EVENTS.transportState, ({ state, direction }) => {
      if (this.isReleasing) {
        return;
      }

      if (['disconnected', 'failed'].includes(state) && this.isChannelJoined && !this.#isConnectionLost) {
        this.#isConnectionLost = true;
        this.#logger.info('connection lost', { channel: 'webrtc-transport', direction });
        this.clientEventEmitter.safeEmit(CLIENT_EVENTS.connectionLost);
      }

      if (state === 'reconnected' && this.isChannelJoined) {
        this.#logger.info('connection restored', { channel: 'webrtc-transport', direction });
      }

      if (state === 'reconnected' && this.isChannelJoined && this.#isConnectionLost) {
        const isAllTransportsConnected = [
          this.network.receiveTransport,
          this.network.sendTransport,
        ].every((tr) => tr?.connectionState === 'connected' || tr?.connectionState === 'new');

        if (!isAllTransportsConnected) {
          return;
        }

        this.#isConnectionLost = false;
        this.clientEventEmitter.safeEmit(CLIENT_EVENTS.connectionRestored);
        this.producerCloseManager.flushPending().catch((error: unknown) => {
          this.#logger.warn('Failed to flush pending remote producers', { error: serializeError(error) });
        });
      }
    });
  }

  private watchDocumentUnload(): void {
    try {
      window.addEventListener('unload', () => {
        this.network.sendTransport?.close();
        this.network.receiveTransport?.close();
      });
    } catch (error) {
      this.#logger.error('Failed to add watchDocumentUnload listener', {
        channelId: this.state.channelId,
        error: serializeError(error),
      });
    }
  }

  private async createSendRecvTransports(): Promise<[LDTransport, LDTransport]> {
    const [sendResult, recvResult] = await Promise.allSettled<LDTransport>([
      this.network.createSendTransport(this.media.mediasoupDevice),
      this.network.createRecvTransport(this.media.mediasoupDevice),
    ]);

    const failed = sendResult.status === 'rejected' || recvResult.status === 'rejected';

    if (failed) {
      this.network.closeSendTransport();
      this.network.closeReceiveTransport();
    }

    if (sendResult.status === 'rejected') {
      throw new OperationError('create_send_transport_failed', sendResult.reason);
    }

    if (recvResult.status === 'rejected') {
      throw new OperationError('create_recv_transport_failed', recvResult.reason);
    }

    return [sendResult.value, recvResult.value];
  }
}

export default Engine;
